/**
 * 认证相关的hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, API_ENDPOINTS, type LoginRequest, type LoginResponse } from '@/lib/api-client'
import { useNavigate } from 'react-router-dom'
import { useViewRoleStore } from '@/stores/view-role'
import { useOrderDetailModal } from '@/stores/order-detail-modal'
import { useTabbarStore } from '@/stores/tabbar'

/**
 * 清除一切「用户态」持久化残留——登录/登出都必须调用。
 * 否则多账号共用浏览器时，上一账号的化身身份、视图角色、**已打开的菜单标签**、
 * 表单草稿会串到下一个账号，造成「以 A 登录却显示 B 身份 / 看到无权限的菜单标签」。
 */
function clearCrossAccountState() {
  useOrderDetailModal.getState().close()
  // 化身(impersonation)
  localStorage.removeItem('impersonation_id')
  localStorage.removeItem('impersonated_user')
  localStorage.removeItem('admin_original_token')
  localStorage.removeItem('admin_original_refresh_token')
  // 用户态 UI 持久化：视图角色 / 菜单标签 / 表单草稿
  localStorage.removeItem('view-role-storage')
  localStorage.removeItem('tabbar-storage')
  localStorage.removeItem('form-cache-storage')
  // 同步重置内存 store（登录是 SPA 跳转、不整页重载，必须显式重置才生效）
  useViewRoleStore.getState().resetViewRole()
  useTabbarStore.getState().reset()
}

// 登录
export function useLogin() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (credentials: LoginRequest) =>
      apiClient.post<LoginResponse>(API_ENDPOINTS.auth.login, credentials),
    onSuccess: (data) => {
      // 清除旧的缓存（防止显示上一个用户的数据）
      queryClient.clear()

      // 清除上一账号的化身/视图角色/菜单标签/表单草稿残留（多账号共用浏览器防串台）
      clearCrossAccountState()

      // 保存新的 token
      localStorage.setItem('access_token', data.accessToken)
      localStorage.setItem('refresh_token', data.refreshToken)

      // 缓存用户角色，用于侧边栏菜单初始化（防止闪烁）
      if (data.roles?.[0]) {
        localStorage.setItem('cached_user_role', data.roles[0])
      }

      // 使用 replace 替换历史记录,防止返回到登录页
      navigate('/dashboard', { replace: true })
    },
  })
}

// 获取当前用户信息
export function useCurrentUser() {
  return useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const user = await apiClient.get(API_ENDPOINTS.auth.me)
      // 缓存用户角色到 localStorage，用于侧边栏菜单初始化（防止闪烁）
      if (user?.roles?.[0]) {
        localStorage.setItem('cached_user_role', user.roles[0])
      }
      return user
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // 5分钟
  })
}

// 退出登录
export function useLogout() {
  const queryClient = useQueryClient()

  const logout = () => {
    // 清除 token 和缓存的用户角色
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('cached_user_role')

    // 清除化身/视图角色/菜单标签/表单草稿残留
    clearCrossAccountState()

    // 清除所有 React Query 缓存
    queryClient.clear()

    // 使用 window.location.href 完全重新加载,清除所有状态和历史记录
    window.location.href = '/login'
  }

  return { logout }
}
